import { useCallback, useEffect, useState } from "react";

import {
  Person,
  Receipt,
  Psychology,
  Event,
  Edit,
  Webhook,
  ContentCopy,
  Download,
  Email,
  Link as LinkIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Skeleton,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  GlobalStyles,
} from "@mui/material";
import { createPortal, flushSync } from "react-dom";
import { useParams, Link } from "react-router-dom";

import { formatPrice } from "@/lib/utils";

import {
  handleApiError,
  ordersService,
  authCodesService,
  aiQcService,
  purchaseReceiptsService,
  type Order,
  type PurchaseReceiptOrderStatus,
  type LabRecommendationsAdminResponse,
  type AIQCReview,
} from "../api";
import { EnhancedReportDisplay } from "../components/ai/enhanced-report-display";
import { EditLabOrderModal } from "../components/orders/edit-lab-order-modal";
import { OrderActivityLog } from "../components/orders/order-activity-log";
import PSCAppointmentHeaderInfo from "../components/orders/psc-appointment-header-info";
import { PurchaseReceiptStatusChip } from "../components/orders/purchase-receipt-status-chip";
import { RelinkJunctionModal } from "../components/orders/relink-junction-modal";
import { SendPurchaseReceiptModal } from "../components/orders/send-purchase-receipt-modal";
import StatusHeaderInfo from "../components/orders/status-header-info";
import { useUser } from "../hooks/use-user";
import { triggerBrowserDownload } from "../lib/export-utils";

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }
    return `${date.toISOString().split("T")[0]} ${date.toTimeString().split(" ")[0]?.slice(0, 5)}`;
  } catch {
    return "Invalid date";
  }
};

const renderWebhookValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "-";
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const flattenWebhookPayload = (
  obj: Record<string, unknown>,
  prefix = "",
): Array<{ key: string; value: unknown }> => {
  const result: Array<{ key: string; value: unknown }> = [];

  Object.entries(obj).forEach(([key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      result.push(
        ...flattenWebhookPayload(value as Record<string, unknown>, fullKey),
      );
    } else {
      result.push({ key: fullKey, value });
    }
  });

  return result;
};

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const [order, setOrder] = useState<Order | null>(null);
  const [recommendations, setRecommendations] =
    useState<LabRecommendationsAdminResponse | null>(null);
  const [qcReview, setQcReview] = useState<AIQCReview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] =
    useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRelinkModalOpen, setIsRelinkModalOpen] = useState(false);
  const [isSendReceiptModalOpen, setIsSendReceiptModalOpen] = useState(false);
  const [receiptStatus, setReceiptStatus] =
    useState<PurchaseReceiptOrderStatus | null>(null);
  const [error, setError] = useState("");
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [resendingAction, setResendingAction] = useState<string | null>(null);
  const [isDownloadingResultsPdf, setIsDownloadingResultsPdf] = useState(false);
  const [isPrintingReport, setIsPrintingReport] = useState(false);
  const [actionSnackbar, setActionSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const canViewPurchaseReceipt =
    user?.role === "admin" || user?.role === "support";

  const refreshReceiptStatus = useCallback(async () => {
    if (!id || !canViewPurchaseReceipt) {
      setReceiptStatus(null);
      return;
    }
    try {
      setReceiptStatus(await purchaseReceiptsService.getForOrder(id));
    } catch {
      setReceiptStatus(null);
    }
  }, [id, canViewPurchaseReceipt]);

  useEffect(() => {
    refreshReceiptStatus();
  }, [refreshReceiptStatus]);

  useEffect(() => {
    let ignore = false;

    const fetchOrderData = async () => {
      if (!id) return;

      setIsLoading(true);
      setError("");
      setOrder(null);
      setRecommendations(null);
      setQcReview(null);

      // Fetch order, recommendations, and QC review in parallel
      const [orderData, recommendationsData, qcReviewData] =
        await Promise.allSettled([
          ordersService.getOrderById(id),
          ordersService.getOrderRecommendations(id).catch(() => null), // Don't fail if no recommendations
          aiQcService.getReviewByOrderId(id).catch(() => null), // Don't fail if no QC review
        ]);

      if (ignore) return;

      if (orderData.status === "fulfilled") {
        setOrder(orderData.value);
      } else {
        setError(handleApiError(orderData.reason));
        setIsLoading(false);
        return;
      }

      setRecommendations(
        recommendationsData.status === "fulfilled"
          ? recommendationsData.value
          : null,
      );
      setQcReview(
        qcReviewData.status === "fulfilled" ? qcReviewData.value : null,
      );
      setIsLoading(false);
    };

    fetchOrderData();

    return () => {
      ignore = true;
    };
  }, [id]);

  const handleGenerateRecommendations = async () => {
    if (!id) return;

    try {
      setIsGeneratingRecommendations(true);
      setError("");
      await ordersService.generateRecommendations(id);

      // Fetch updated recommendations
      const updatedRecommendations =
        await ordersService.getOrderRecommendations(id);
      setRecommendations(updatedRecommendations);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsGeneratingRecommendations(false);
    }
  };

  const handleOrderUpdate = (updatedOrder: Order) => {
    setOrder(updatedOrder);
  };

  const handleCopyToClipboard = async (value: unknown) => {
    const textToCopy = renderWebhookValue(value);
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySnackbarOpen(true);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  const handleResendEmail = async (
    actionKey: string,
    action: () => Promise<{ message: string }>,
  ) => {
    try {
      setResendingAction(actionKey);
      const result = await action();
      setActionSnackbar({
        open: true,
        message: result.message,
        severity: "success",
      });
    } catch (err) {
      setActionSnackbar({
        open: true,
        message: handleApiError(err),
        severity: "error",
      });
    } finally {
      setResendingAction(null);
    }
  };

  const handleDownloadResultsPdf = async () => {
    if (!id) return;

    try {
      setIsDownloadingResultsPdf(true);
      const { blob, filename } = await ordersService.downloadResultsPdf(id);
      triggerBrowserDownload({ blob, filename });
    } catch (err) {
      setActionSnackbar({
        open: true,
        message: handleApiError(err),
        severity: "error",
      });
    } finally {
      setIsDownloadingResultsPdf(false);
    }
  };

  const handleExportReport = () => {
    flushSync(() => {
      setIsPrintingReport(true);
    });
    window.print();
    setIsPrintingReport(false);
  };

  const canEdit = user?.role === "admin" || user?.role === "support";
  const canRelinkJunction = canEdit || user?.role === "doctor";
  const canSendPurchaseReceipt = user?.role === "admin";

  const reportRecommendations = recommendations
    ? {
        hasRecommendations: recommendations.hasRecommendations,
        message: recommendations.message,
        recommendations: recommendations.recommendations,
        ...(recommendations.generatedAt && {
          generatedAt: recommendations.generatedAt,
        }),
        ...(recommendations.metadata && {
          metadata: {
            modelUsed: recommendations.metadata.modelUsed,
            promptHash: recommendations.metadata.promptHash || "",
            responseType: recommendations.metadata.responseType || "",
            status: recommendations.metadata.status || "",
            ...(recommendations.metadata.tokensUsed !== undefined && {
              tokensUsed: recommendations.metadata.tokensUsed,
            }),
            ...(recommendations.metadata.processingTimeMs !== undefined && {
              processingTimeMs: recommendations.metadata.processingTimeMs,
            }),
            ...(recommendations.metadata.prompt && {
              prompt: recommendations.metadata.prompt,
            }),
          },
        }),
      }
    : null;

  const reportPatientName =
    order && order.user.firstName && order.user.lastName
      ? `${order.user.firstName} ${order.user.lastName}`
      : (order?.user.email ?? "");

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "in_progress":
        return "primary";
      case "received":
        return "secondary";
      case "created":
        return "info";
      case "purchased":
        return "warning";
      case "failed":
        return "error";
      case "cancelled":
        return "error";
      case "deleted":
        return "default";
      default:
        return "default";
    }
  };

  const getAppointmentStatusColor = (status: string | null) => {
    if (!status) return "default";
    switch (status.toLowerCase()) {
      case "scheduled":
      case "confirmed":
        return "success";
      case "cancelled":
        return "error";
      case "completed":
        return "primary";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 3 }}>
        Order Details
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Skeleton variant="text" width={200} height={32} />
                <Skeleton
                  variant="text"
                  width={300}
                  height={24}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Skeleton variant="text" width={200} height={32} />
                <Skeleton
                  variant="text"
                  width={300}
                  height={24}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : order ? (
        <Box
          sx={{
            maxWidth: "1200px",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Receipt />
                  <Typography variant="h6" component="h2">
                    Order Information
                  </Typography>
                </Box>
                {canEdit && order.status !== "deleted" && (
                  <Tooltip title="Edit lab order">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                )}
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Order ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {order.id}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Package ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {order.packageId || "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Junction Order ID
                  </Typography>
                  {order.junctionOrderId ? (
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {order.junctionOrderId}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        -
                      </Typography>
                      {canRelinkJunction && order.status !== "deleted" && (
                        <Tooltip title="Pick a Junction order for this user and link it">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LinkIcon />}
                            onClick={() => setIsRelinkModalOpen(true)}
                          >
                            Link
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <StatusHeaderInfo />
                  </Box>
                  <Chip
                    label={order.status.toUpperCase()}
                    color={getStatusColor(order.status)}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Price
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatPrice(order.price, order.currency)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(order.createdAt)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Person />
                <Typography variant="h6" component="h2">
                  User Information
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Email
                  </Typography>
                  <Link
                    to={`/users/${order.user.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ "&:hover": { textDecoration: "underline" } }}
                    >
                      {order.user.email}
                    </Typography>
                  </Link>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Full Name
                  </Typography>
                  <Typography variant="body2">
                    {order.user.firstName && order.user.lastName
                      ? `${order.user.firstName} ${order.user.lastName}`
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Role
                  </Typography>
                  <Chip
                    label={order.user.role.toUpperCase()}
                    color={
                      order.user.role === "admin"
                        ? "error"
                        : order.user.role === "doctor"
                          ? "primary"
                          : "default"
                    }
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Verified
                  </Typography>
                  <Chip
                    label={order.user.isVerified ? "Verified" : "Unverified"}
                    color={order.user.isVerified ? "success" : "warning"}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    User ID
                  </Typography>
                  <Link
                    to={`/users/${order.user.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{
                        fontFamily: "monospace",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {order.user.id}
                    </Typography>
                  </Link>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Event />
                <Typography variant="h6" component="h2">
                  PSC Appointment Information
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Appointment Status
                    </Typography>
                    <PSCAppointmentHeaderInfo />
                  </Box>
                  <Tooltip
                    title={
                      order.pscAppointmentStatus
                        ? `PSC appointment is ${order.pscAppointmentStatus.toLowerCase()}`
                        : "No PSC appointment scheduled for this order"
                    }
                    arrow
                  >
                    <Chip
                      label={
                        order.pscAppointmentStatus
                          ? order.pscAppointmentStatus.toUpperCase()
                          : "NONE"
                      }
                      color={getAppointmentStatusColor(
                        order.pscAppointmentStatus,
                      )}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}></Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          {canEdit && id && order.status !== "deleted" && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Email />
                  <Typography variant="h6" component="h2">
                    Quick Actions
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={resendingAction !== null}
                    startIcon={
                      resendingAction === "purchaseConfirmation" ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Email />
                      )
                    }
                    onClick={() =>
                      handleResendEmail("purchaseConfirmation", () =>
                        ordersService.resendPurchaseConfirmation(id),
                      )
                    }
                  >
                    Resend Purchase Confirmation
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={resendingAction !== null}
                    startIcon={
                      resendingAction === "labResults" ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Email />
                      )
                    }
                    onClick={() =>
                      handleResendEmail("labResults", () =>
                        ordersService.resendLabResultsNotification(id),
                      )
                    }
                  >
                    Resend Lab Results Notification
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={resendingAction !== null}
                    startIcon={
                      resendingAction === "reportEmail" ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Email />
                      )
                    }
                    onClick={() =>
                      handleResendEmail("reportEmail", () =>
                        ordersService.resendReportEmail(id),
                      )
                    }
                  >
                    Resend Report Email
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={resendingAction !== null}
                    startIcon={
                      resendingAction === "onboardingReminder" ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Email />
                      )
                    }
                    onClick={() =>
                      handleResendEmail("onboardingReminder", () =>
                        ordersService.resendOnboardingReminder(id),
                      )
                    }
                  >
                    Resend Onboarding Reminder
                  </Button>
                  {!order.user.isVerified && (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={resendingAction !== null}
                      startIcon={
                        resendingAction === "verification" ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Email />
                        )
                      }
                      onClick={() =>
                        handleResendEmail("verification", () =>
                          authCodesService.resendVerificationEmail(
                            order.user.email,
                          ),
                        )
                      }
                    >
                      Resend Verification Email
                    </Button>
                  )}
                  {order.junctionOrderId && (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={isDownloadingResultsPdf}
                      startIcon={
                        isDownloadingResultsPdf ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Download />
                        )
                      }
                      onClick={handleDownloadResultsPdf}
                    >
                      Download Results PDF
                    </Button>
                  )}
                </Box>
                {(receiptStatus || canSendPurchaseReceipt) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1.5,
                      mt: 2,
                      pt: 2,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Purchase receipt
                    </Typography>
                    {receiptStatus && (
                      <PurchaseReceiptStatusChip receipt={receiptStatus} />
                    )}
                    {canSendPurchaseReceipt && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={
                          resendingAction !== null ||
                          receiptStatus?.status === "sent"
                        }
                        startIcon={<Email />}
                        onClick={() => setIsSendReceiptModalOpen(true)}
                      >
                        Send Purchase Receipt
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Order Activity Log */}
          {id && <OrderActivityLog orderId={id} />}

          {/* Webhook Payload Card */}
          {order.webhookPayload && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Webhook />
                  <Typography variant="h6" component="h2">
                    Webhook Payload
                  </Typography>
                </Box>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: 500,
                    border: 1,
                    borderColor: "grey.200",
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            bgcolor: "grey.100",
                            width: "40%",
                          }}
                        >
                          Key
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold", bgcolor: "grey.100" }}
                        >
                          Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {flattenWebhookPayload(order.webhookPayload).map(
                        ({ key, value }, index) => (
                          <TableRow
                            key={index}
                            onMouseEnter={() => setHoveredRowIndex(index)}
                            onMouseLeave={() => setHoveredRowIndex(null)}
                            sx={{ "&:hover": { bgcolor: "grey.50" } }}
                          >
                            <TableCell
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.75rem",
                                color: "primary.main",
                                verticalAlign: "top",
                              }}
                            >
                              {key}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.75rem",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  {renderWebhookValue(value)}
                                </Box>
                                {hoveredRowIndex === index && (
                                  <Tooltip
                                    title="Copy to clipboard"
                                    placement="left"
                                  >
                                    <IconButton
                                      size="small"
                                      aria-label="Copy to clipboard"
                                      onClick={() =>
                                        handleCopyToClipboard(value)
                                      }
                                      sx={{
                                        padding: 0.25,
                                        minWidth: "auto",
                                        opacity: 0.6,
                                        "&:hover": { opacity: 1 },
                                      }}
                                    >
                                      <ContentCopy sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* AI Prompt Details Card */}
          {recommendations?.metadata?.prompt && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Psychology />
                  <Typography variant="h6" component="h2">
                    AI Prompt Details
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    p: 2,
                    maxHeight: 300,
                    overflow: "auto",
                    border: 1,
                    borderColor: "grey.200",
                  }}
                >
                  <Typography
                    component="pre"
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {recommendations.metadata.prompt}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          <Box>
            {/* QC Review Status Alert */}
            {qcReview && (
              <Alert
                severity={
                  qcReview.status === "approved"
                    ? "success"
                    : qcReview.status === "rejected"
                      ? "error"
                      : "info"
                }
                sx={{ mb: 2 }}
                action={
                  <Tooltip
                    title={`Review ${qcReview.status.toUpperCase()}${qcReview.reviewedBy ? ` by ${qcReview.reviewedBy.firstName} ${qcReview.reviewedBy.lastName}` : ""} at ${formatDate(qcReview.updatedAt)}`}
                    placement="left"
                  >
                    <Button
                      component={Link}
                      to={`/ai-qc/review/${qcReview.id}`}
                      size="small"
                      variant="outlined"
                      color="inherit"
                    >
                      Review Report
                    </Button>
                  </Tooltip>
                }
              >
                {qcReview.status === "pending"
                  ? `AI Report is pending doctor review. Generated at ${formatDate(qcReview.createdAt)}.`
                  : `AI Report has been ${qcReview.status.toUpperCase()}${qcReview.reviewedBy ? ` by ${qcReview.reviewedBy.firstName} ${qcReview.reviewedBy.lastName}` : ""}.`}
              </Alert>
            )}

            {/* Generate Report Button */}
            {order.status === "completed" &&
              !recommendations?.hasRecommendations && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    onClick={handleGenerateRecommendations}
                    disabled={isGeneratingRecommendations}
                    variant="outlined"
                    startIcon={
                      isGeneratingRecommendations ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Psychology />
                      )
                    }
                  >
                    {isGeneratingRecommendations
                      ? "Generating..."
                      : "Generate Report"}
                  </Button>
                </Box>
              )}

            {/* Enhanced Report Display */}
            {order.status !== "completed" ? (
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    AI Report is only available for completed orders.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <>
                <EnhancedReportDisplay
                  recommendations={reportRecommendations}
                  loading={isGeneratingRecommendations}
                  patientName={reportPatientName}
                  onExport={handleExportReport}
                />
                {isPrintingReport &&
                  createPortal(
                    <Box
                      id="lab-report-print-area"
                      sx={{ display: "none", displayPrint: "block" }}
                    >
                      <GlobalStyles
                        styles={{
                          "@media print": {
                            "body > *:not(#lab-report-print-area)": {
                              display: "none",
                            },
                          },
                        }}
                      />
                      <EnhancedReportDisplay
                        recommendations={reportRecommendations}
                        loading={isGeneratingRecommendations}
                        patientName={reportPatientName}
                      />
                    </Box>,
                    document.body,
                  )}
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Order not found
          </Typography>
        </Box>
      )}

      {/* Edit Lab Order Modal */}
      {order && (
        <EditLabOrderModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          order={order}
          onUpdate={handleOrderUpdate}
        />
      )}

      {order && id && (
        <RelinkJunctionModal
          open={isRelinkModalOpen}
          onClose={() => setIsRelinkModalOpen(false)}
          orderId={id}
          onLinked={handleOrderUpdate}
        />
      )}

      {order && id && canSendPurchaseReceipt && (
        <SendPurchaseReceiptModal
          open={isSendReceiptModalOpen}
          onClose={() => setIsSendReceiptModalOpen(false)}
          orderId={id}
          customerEmail={order.user.email}
          onSent={(result) => {
            setActionSnackbar({
              open: true,
              message: result.message,
              severity:
                result.mode === "recorded" && result.status !== "sent"
                  ? "error"
                  : "success",
            });
            refreshReceiptStatus();
          }}
        />
      )}

      {/* Copy to Clipboard Snackbar */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Action Result Snackbar */}
      <Snackbar
        open={actionSnackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setActionSnackbar((prev) => ({ ...prev, open: false }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setActionSnackbar((prev) => ({ ...prev, open: false }))
          }
          severity={actionSnackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {actionSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
